import { Frame, ArrowUpRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { getRouteState } from "@/lib/navigation";

function stripHtml(html) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").replace(/&[^;]+;/g, " ").trim();
}

function resolveImage(product) {
  if (product.images?.length > 0 && product.images[0].url) {
    return product.images[0];
  }
  const variantWithImg = product.variants?.find(
    (v) => v.image && v.image.url
  );
  if (variantWithImg) return variantWithImg.image;
  return null;
}

function formatPrice(price, maxPrice, productType) {
  if (!price) return null;
  const amt = parseFloat(price.amount);
  if (isNaN(amt)) return null;
  const sym = price.currencyCode === "GBP" ? "\u00a3" : "$";
  const maxAmt = maxPrice ? parseFloat(maxPrice.amount) : amt;
  if (amt === 0 && maxAmt === 0) return "Price on request";
  // Murals are priced per square metre — surface this clearly
  const isMural = (productType || "").toLowerCase().includes("mural");
  const unit = isMural ? "/m\u00b2" : "";
  const formatted = `From ${sym}${amt.toFixed(0)}${unit}`;
  return amt === maxAmt ? `${sym}${amt.toFixed(isMural ? 0 : 2)}${unit}` : formatted;
}

export default function ProductCard({ product, basePath, variant = "default" }) {
  const location = useLocation();
  const image = resolveImage(product);
  const priceStr = formatPrice(
    product.priceRange?.minVariantPrice,
    product.priceRange?.maxVariantPrice,
    product.productType || product.product_type
  );
  const desc = stripHtml(product.description);

  // Always link within the storefront — /products/:handle resolves to the correct route
  const href = basePath
    ? `${basePath}/${product.handle}`
    : `/products/${product.handle}`;

  const Wrapper = Link;
  const wrapperProps = { to: href, state: getRouteState(location) };

  /* ── MURAL VARIANT ── */
  if (variant === "mural") {
    return (
      <Wrapper
        {...wrapperProps}
        className="group relative block rounded-sm overflow-hidden transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_-12px_rgba(203,160,82,0.15)]"
        data-testid={`product-card-${product.handle}`}
      >
        <div className="aspect-[16/9] overflow-hidden bg-brand-bg relative">
          {image ? (
            <>
              <img
                src={image.url}
                alt={image.altText || product.title}
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                loading="lazy"
              />
              {/* bottom gradient for text legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14]/90 via-[#0A0D14]/20 to-transparent" />
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-brand-surface to-brand-bg relative flex flex-col justify-end px-6 pb-5">
              <div className="absolute inset-0 border border-brand-border/10" />
              {desc && (
                <p className="relative text-[12px] text-brand-text-secondary/30 leading-relaxed line-clamp-2 mb-3">
                  {desc.slice(0, 100)}
                </p>
              )}
            </div>
          )}

          {/* overlay info — always on the image */}
          <div className="absolute bottom-0 left-0 right-0 px-5 pb-4 pt-8">
            <h3 className="font-body text-[14px] font-medium leading-snug text-white group-hover:text-brand-gold transition-colors duration-300 line-clamp-2">
              {product.title}
            </h3>
            <p className="mt-1 text-[12px] text-brand-gold/70 italic tracking-wide">
              {priceStr ? `${priceStr} \u00b7 Made to measure` : "Made to measure"}
            </p>
          </div>

          {/* hover badge */}
          <div className="absolute top-3 right-3 w-7 h-7 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <ArrowUpRight size={13} className="text-brand-gold" />
          </div>
        </div>
      </Wrapper>
    );
  }

  /* ── CANVAS VARIANT ── */
  if (variant === "canvas") {
    return (
      <Wrapper
        {...wrapperProps}
        className="group relative block bg-brand-surface border border-brand-border rounded-sm overflow-hidden transition-[transform,box-shadow,border-color] duration-300 hover:border-brand-gold/40 hover:-translate-y-0.5 hover:shadow-[0_10px_36px_-14px_rgba(203,160,82,0.16)]"
        data-testid={`product-card-${product.handle}`}
      >
        <div className="aspect-[16/9] overflow-hidden bg-brand-bg relative">
          {image ? (
            <>
              <img
                src={image.url}
                alt={image.altText || product.title}
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14]/65 via-[#0A0D14]/10 to-transparent" />
            </>
          ) : (
            <div className="w-full h-full flex flex-col justify-end bg-gradient-to-b from-brand-surface via-brand-surface to-brand-bg relative px-6 pb-5 pt-8">
              <div className="absolute inset-4 border border-brand-border/20 rounded-[1px]" />
              {desc ? (
                <p className="relative text-[13px] text-brand-text-secondary/45 leading-relaxed line-clamp-3 max-w-md">
                  {desc.slice(0, 150)}
                </p>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <Frame
                    size={22}
                    strokeWidth={1}
                    className="text-brand-border/30"
                  />
                </div>
              )}
            </div>
          )}

          <div className="absolute top-3 right-3 w-7 h-7 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <ArrowUpRight size={13} className="text-brand-gold" />
          </div>
        </div>

        <div className="px-5 pt-4 pb-5">
          <h3 className="font-body text-[15px] font-medium leading-[1.35] text-brand-text group-hover:text-brand-gold transition-colors duration-300 line-clamp-2">
            {product.title}
          </h3>
          {priceStr && (
            <p className={`mt-1.5 text-[13px] font-medium tracking-wide ${
              priceStr === "Price on request"
                ? "text-brand-gold/70 italic"
                : "text-brand-text-secondary"
            }`}>
              {priceStr}
            </p>
          )}
          {(
          <p
            className="mt-2 text-[11px] uppercase tracking-[0.16em] text-brand-gold/75"
            data-testid={`product-card-configure-hint-${product.handle}`}
          >
            Open details &amp; configure
          </p>
        )}
      </div>
    </Wrapper>
  );
  }

  /* ── DEFAULT VARIANT (canvas / general) ── */
  return (
    <Wrapper
      {...wrapperProps}
      className="group relative block bg-brand-surface border border-brand-border rounded-sm overflow-hidden transition-[transform,box-shadow,border-color] duration-300 hover:border-brand-gold/40 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_-12px_rgba(203,160,82,0.12)]"
      data-testid={`product-card-${product.handle}`}
    >
      {/* image */}
      <div className="aspect-[4/5] overflow-hidden bg-brand-bg">
        {image ? (
          <img
            src={image.url}
            alt={image.altText || product.title}
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col justify-end bg-gradient-to-b from-brand-surface via-brand-surface to-brand-bg relative px-5 pb-5 pt-8">
            <div className="absolute inset-3 border border-brand-border/20 rounded-[1px]" />
            {desc ? (
              <p className="relative text-[13px] text-brand-text-secondary/50 leading-relaxed line-clamp-5">
                {desc.slice(0, 140)}
              </p>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <Frame
                  size={20}
                  strokeWidth={1}
                  className="text-brand-border/30"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* info */}
      <div className="px-4 pt-3.5 pb-4">
        <h3 className="font-body text-[13px] font-medium leading-[1.35] text-brand-text group-hover:text-brand-gold transition-colors duration-300 line-clamp-2">
          {product.title}
        </h3>
        {priceStr && (
          <p className={`mt-1.5 text-[13px] font-medium tracking-wide ${
            priceStr === "Price on request"
              ? "text-brand-gold/70 italic"
              : "text-brand-text-secondary"
          }`}>
            {priceStr}
          </p>
        )}
        <p
          className="mt-2 text-[11px] uppercase tracking-[0.16em] text-brand-gold/75"
          data-testid={`product-card-configure-hint-${product.handle}`}
        >
          Open details &amp; configure
        </p>
      </div>

      {/* hover badge */}
      <div className="absolute top-3 right-3 w-7 h-7 bg-brand-bg/70 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <ArrowUpRight size={13} className="text-brand-gold" />
      </div>
    </Wrapper>
  );
}
